import { useCallback, useState } from 'react';

function loadFrozenKeys(storageKey, fallback) {
  if (!storageKey) return fallback;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return fallback;
    return parsed.filter((k) => typeof k === 'string');
  } catch {
    return fallback;
  }
}

function persistFrozenKeys(storageKey, keys) {
  if (!storageKey) return;
  try {
    localStorage.setItem(storageKey, JSON.stringify(keys));
  } catch (e) {
    console.warn('Failed to save frozen columns:', e);
  }
}

/**
 * Personalized frozen (pinned-left) columns, persisted in localStorage.
 *
 * @param {string} storageKey
 * @param {string[]} [defaultKeys]
 * @param {{ max?: number, validKeys?: string[] }} [options]
 */
const useFrozenColumns = (storageKey, defaultKeys = [], options = {}) => {
  const max = options.max ?? 6;
  const valid = options.validKeys ? new Set(options.validKeys) : null;

  const [frozenKeys, setFrozenKeys] = useState(() => {
    const loaded = loadFrozenKeys(storageKey, defaultKeys);
    const filtered = valid ? loaded.filter((k) => valid.has(k)) : loaded;
    return new Set(filtered.slice(0, max));
  });

  const toggleFrozen = useCallback((key) => {
    setFrozenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else if (next.size < max) next.add(key);
      persistFrozenKeys(storageKey, [...next]);
      return next;
    });
  }, [storageKey, max]);

  const clearFrozen = useCallback(() => {
    setFrozenKeys(new Set());
    persistFrozenKeys(storageKey, []);
  }, [storageKey]);

  return { frozenKeys, toggleFrozen, clearFrozen, maxFrozen: max };
};

export default useFrozenColumns;
